import {TranslatableEntry} from 'features/Translations/TranslatableResource/types';
import {getHumanReadableTranslatableThemeKey} from 'features/Translations/TranslatableResource/utils';
import sortBy from 'lodash/sortBy';
import uniq from 'lodash/uniq';
import {TranslatableResourceType} from 'types';
import {parseGid, parseGidWithParams} from '@web-utilities/other';
import {AppQuery} from 'utilities/url';
import {I18n} from '@shopify/react-i18n';

import {cookieBannerGrouping} from '../../utils/cookieBannerGrouping';

import {MISC_CATEGORY} from './constants';
import {TranslatableResourceListItem} from './types';
import type {NavigationQueryData} from './components';

export function getThemeAssetCategoryName(category: string) {
  if (category === 'shopify') {
    return 'Checkout and system';
  } else {
    return (
      category.charAt(0).toUpperCase() + category.slice(1).toLowerCase()
    ).replace(/_/g, ' ');
  }
}

export function getCookieBannerCategoryName(category: string, i18n?: I18n) {
  if (i18n) {
    return i18n.translate(`SectionLabels.cookie_banner_${category}`);
  } else {
    // Fallback for edge cases without an i18n object like unit tests etc, shouldn't really be
    // used in production:
    return (
      category.charAt(0).toUpperCase() + category.slice(1).toLowerCase()
    ).replace(/_/g, ' ');
  }
}

export function createResourceListItems(
  resourceType: TranslatableResourceType,
  translatableEntries: TranslatableEntry[],
  searchResults: NavigationQueryData.ShopSearchEdgesNode[],
  i18n?: I18n,
): TranslatableResourceListItem[] {
  if (
    resourceType ===
    TranslatableResourceType.OnlineStoreThemeSettingsDataSections
  ) {
    const categories = translatableEntries
      .reduce<string[]>((sidebarKeys, {key}) => {
        const templateOrSectionKey = key.includes('.json')
          ? key.split('.json')[0]
          : key.split('.').slice(0, 2).join('.');
        if (!sidebarKeys.includes(templateOrSectionKey)) {
          sidebarKeys.push(templateOrSectionKey);
        }

        return sidebarKeys;
      }, [])
      .map((sidebarKey) => ({
        id: sidebarKey,
        name: getHumanReadableTranslatableThemeKey(sidebarKey),
      }));

    return sortBy(categories, 'name');
  } else if (
    resourceType === TranslatableResourceType.OnlineStoreThemeLocaleContent
  ) {
    let topLevelKeyExists = false;
    const assetCategories = uniq(
      translatableEntries
        .filter(({key}) => {
          if (key.split('.').length === 1) {
            topLevelKeyExists = true;
            return false;
          }

          //  Don't care about section keys for asset categories
          if (key.startsWith('section.')) {
            return false;
          }

          return true;
        })
        .map(({key}) => key.split('.')[0]),
    ).map((category) => {
      return {
        id: category,
        name: getThemeAssetCategoryName(category),
      };
    });

    if (topLevelKeyExists) {
      assetCategories.push({
        id: MISC_CATEGORY,
        name: 'Misc',
      });
    }

    return sortBy(assetCategories, 'name');
  } else if (resourceType === TranslatableResourceType.CookieBanner) {
    const assetCategories = uniq(
      Object.entries(cookieBannerGrouping).map((item) => item[1].section),
    ).map((category) => {
      return {
        id: category,
        name: getCookieBannerCategoryName(category, i18n),
      };
    });

    return searchResults.length ? sortBy(assetCategories, 'name') : [];
  } else if (isSearchableThemeResourceType(resourceType)) {
    return searchResults.map(({id, title}) => {
      const {id: themeResourceName} = parseGidWithParams(id);
      let name = getHumanReadableTranslatableThemeKey(
        decodeURIComponent(themeResourceName).replace(/\+/g, ' '),
      );
      if (
        resourceType ===
          TranslatableResourceType.OnlineStoreThemeJsonTemplate &&
        name === 'Index'
      ) {
        name = 'Index - Home page';
      }
      if (resourceType === TranslatableResourceType.OnlineStoreThemeAppEmbed) {
        name = title;
      }
      return {
        id,
        name,
      };
    });
  } else {
    return searchResults.map(({id, title: name}) => ({
      id,
      name,
    }));
  }
}

export const buildQueryParams = ({
  id,
  item,
  resourceType,
  ...rest
}: AppQuery & {id: string; resourceType: TranslatableResourceType}) => {
  if (
    isUnsearchableThemeResourceType(resourceType) ||
    resourceType === TranslatableResourceType.CookieBanner
  ) {
    return {
      ...rest,
      id: parseGid(id),
      item,
      themeId: undefined,
      firstSettingId: undefined,
      appEmbedUuid: undefined,
    };
  } else if (
    [
      TranslatableResourceType.OnlineStoreThemeJsonTemplate,
      TranslatableResourceType.OnlineStoreThemeSectionGroup,
    ].includes(resourceType)
  ) {
    const parsedGid = parseGidWithParams(id);
    return {
      ...rest,
      id: parsedGid.id,
      themeId: parsedGid.params.themeId,
      firstSettingId: undefined,
      appEmbedUuid: undefined,
    };
  } else if (
    resourceType === TranslatableResourceType.OnlineStoreThemeSettingsCategory
  ) {
    const parsedGid = parseGidWithParams(id);
    return {
      ...rest,
      id: parsedGid.id,
      themeId: parsedGid.params.themeId,
      firstSettingId: parsedGid.params.firstSettingId,
      appEmbedUuid: undefined,
    };
  } else if (
    resourceType === TranslatableResourceType.OnlineStoreThemeAppEmbed
  ) {
    const parsedGid = parseGidWithParams(id);
    return {
      ...rest,
      id: parsedGid.id,
      themeId: parsedGid.params.themeId,
      appEmbedUuid: parsedGid.params.appEmbedUuid,
      firstSettingId: undefined,
    };
  } else {
    return {
      ...rest,
      id: parseGid(id),
      themeId: undefined,
      firstSettingId: undefined,
      appEmbedUuid: undefined,
    };
  }
};

export const isUnsearchableThemeResourceType = (
  resourceType: TranslatableResourceType,
) =>
  [
    TranslatableResourceType.OnlineStoreThemeLocaleContent,
    TranslatableResourceType.OnlineStoreThemeSettingsDataSections,
  ].includes(resourceType);

export const isSearchableThemeResourceType = (
  resourceType: TranslatableResourceType,
) =>
  [
    TranslatableResourceType.OnlineStoreThemeJsonTemplate,
    TranslatableResourceType.OnlineStoreThemeSectionGroup,
    TranslatableResourceType.OnlineStoreThemeSettingsCategory,
    TranslatableResourceType.OnlineStoreThemeAppEmbed,
  ].includes(resourceType);

export const isResourceTypesWithNoImage = (
  resourceType: TranslatableResourceType,
) =>
  [
    TranslatableResourceType.OnlineStoreBlog,
    TranslatableResourceType.OnlineStorePage,
    TranslatableResourceType.ShopPolicy,
    TranslatableResourceType.OnlineStoreMenu,
    TranslatableResourceType.EmailTemplate,
    TranslatableResourceType.PackingSlipTemplate,
    TranslatableResourceType.DeliveryProfile,
    TranslatableResourceType.OnlineStoreThemeJsonTemplate,
    TranslatableResourceType.OnlineStoreThemeSectionGroup,
    TranslatableResourceType.OnlineStoreThemeSettingsCategory,
    TranslatableResourceType.OnlineStoreThemeLocaleContent,
    TranslatableResourceType.OnlineStoreThemeSettingsDataSections,
    TranslatableResourceType.OnlineStoreThemeAppEmbed,
    TranslatableResourceType.Metaobject,
    TranslatableResourceType.OnlineStoreFilterSetting,
    TranslatableResourceType.Shop,
    TranslatableResourceType.CookieBanner,
  ].includes(resourceType);

export const isResourceTypeWithoutSidebarPagination = (
  resourceType: TranslatableResourceType,
) =>
  [
    TranslatableResourceType.ShopPolicy,
    TranslatableResourceType.EmailTemplate,
    TranslatableResourceType.PackingSlipTemplate,
    TranslatableResourceType.DeliveryProfile,
    TranslatableResourceType.OnlineStoreFilterSetting,
    TranslatableResourceType.OnlineStoreThemeLocaleContent,
    TranslatableResourceType.CookieBanner,
    TranslatableResourceType.OnlineStoreThemeSettingsDataSections,
    TranslatableResourceType.Shop,
  ].includes(resourceType);
